/**
 * Remembered call device choices — persisted in localStorage so a call always
 * uses the **OS default** mic / speaker / camera UNLESS the user has manually
 * switched to a specific device, in which case that choice is remembered across
 * calls and across app restarts.
 *
 * "Default" is represented by the ABSENCE of a stored id (or the `'default'`
 * sentinel deviceId that Chromium exposes) — both resolve to "follow the OS
 * default device". A stored non-default id is used exactly, with a graceful
 * fallback to the default if that device has since disappeared (handled by the
 * caller — see `useCall` mic/camera acquisition and `applyAudioOutput` below).
 *
 * This mirrors the persistence style of `backgroundFx.ts` (try/catch around
 * every localStorage touch so a blocked store — private mode / a locked-down
 * webview — never throws).
 */

export type CallDeviceKind = "audioinput" | "audiooutput" | "videoinput";

const STORAGE_KEY: Record<CallDeviceKind, string> = {
  audioinput: "xop.call.device.audioinput",
  audiooutput: "xop.call.device.audiooutput",
  videoinput: "xop.call.device.videoinput",
};

/** The Chromium sentinel deviceId meaning "follow the OS default device". */
export const DEFAULT_DEVICE_ID = "default";

/** True when an id means "use the OS default" — unset, empty, or the sentinel. */
export function isDefaultDeviceId(id: string | null | undefined): boolean {
  return !id || id === DEFAULT_DEVICE_ID;
}

/** Read the remembered device id for a kind (`null` = follow the OS default). */
export function loadDevicePref(kind: CallDeviceKind): string | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY[kind]);
    return v && v.length > 0 ? v : null;
  } catch {
    /* localStorage blocked (private mode / webview) */
    return null;
  }
}

/**
 * Persist a manual device choice. Storing the `'default'` sentinel (or an empty
 * id) CLEARS the pref — picking "default" means "go back to following the OS
 * default device", NOT "pin whichever device happens to be default right now".
 */
export function saveDevicePref(kind: CallDeviceKind, id: string | null): void {
  try {
    if (isDefaultDeviceId(id)) localStorage.removeItem(STORAGE_KEY[kind]);
    else localStorage.setItem(STORAGE_KEY[kind], id as string);
  } catch {
    /* non-fatal */
  }
}

/**
 * A `getUserMedia` audio/video constraint that honours a remembered device:
 * `true` (the OS default) when unset/default, else an exact-device constraint.
 */
export function deviceConstraint(
  id: string | null,
): true | { deviceId: { exact: string } } {
  return isDefaultDeviceId(id) ? true : { deviceId: { exact: id as string } };
}

/**
 * Whether choosing the audio OUTPUT (speaker) device is supported here.
 * `HTMLMediaElement.setSinkId` exists on Chromium / WebView2 (Windows desktop) /
 * Firefox but NOT on WKWebView (the macOS desktop shell) or older Safari — there
 * the speaker always follows the OS default and the picker is hidden.
 */
export function speakerSelectionSupported(): boolean {
  try {
    const proto = (
      globalThis as { HTMLMediaElement?: { prototype?: { setSinkId?: unknown } } }
    ).HTMLMediaElement?.prototype;
    return typeof proto?.setSinkId === "function";
  } catch {
    return false;
  }
}

/**
 * Apply an output-device (speaker) choice to a media element. No-op when the
 * platform lacks `setSinkId`. Best-effort — a rejected `setSinkId` (device gone,
 * not permitted) is swallowed so the call keeps its current sink. An empty sink
 * id selects the OS default output.
 */
export async function applyAudioOutput(
  el: (HTMLMediaElement & { setSinkId?: (id: string) => Promise<void> }) | null,
  id: string | null,
): Promise<void> {
  if (!el || typeof el.setSinkId !== "function") return;
  try {
    await el.setSinkId(isDefaultDeviceId(id) ? "" : (id as string));
  } catch {
    /* device gone / not permitted — keep the current sink */
  }
}
